import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getMenus, createMenu, deleteMenu, updateMenu, parseMenu, parseMenuImages, importMenu, getVenue } from "@/api/endpoints";
import { UpgradeDialog, isPlanError } from "@/components/upgrade-dialog";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import {
  Plus,
  Trash2,
  ArrowRight,
  UtensilsCrossed,
  Download,
  Sparkles,
  Loader2,
  Check,
} from "lucide-react";

export function MenusPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [menuName, setMenuName] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const { data: menus, isLoading } = useQuery({
    queryKey: ["menus", venueId],
    queryFn: () => getMenus(venueId!),
    enabled: !!venueId,
  });

  const createMutation = useMutation({
    mutationFn: () => createMenu(venueId!, { name: menuName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menus", venueId] });
      setOpen(false);
      setMenuName("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMenu,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menus", venueId] }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateMenu(id, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menus", venueId] }),
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("menus")}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Download className="mr-2 size-4" />{t("import_menu")}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger
            render={<Button><Plus className="mr-2 size-4" />{t("new_menu")}</Button>}
          />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("create_new_menu")}</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate();
              }}
              className="space-y-4"
            >
              <div>
                <Label>{t("menu_name")}</Label>
                <Input
                  value={menuName}
                  onChange={(e) => setMenuName(e.target.value)}
                  placeholder="Ornek: Ana Menu, Icecek Menusu"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending}>
                {createMutation.isPending ? t("creating") : t("create_venue")}
              </Button>
              {createMutation.isError && (
                <p className="text-sm text-destructive">
                  {t("menu_limit")}
                </p>
              )}
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ImportMenuDialog open={importOpen} onClose={() => setImportOpen(false)} venueId={venueId!} />

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>
      ) : menus?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <UtensilsCrossed className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("no_menus")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {menus?.map((menu) => (
            <div
              key={menu.id}
              className="flex items-center justify-between rounded-xl border bg-card p-4 transition-shadow hover:shadow-sm"
            >
              <div
                className="flex flex-1 cursor-pointer items-center gap-4"
                onClick={() => navigate(`/venues/${venueId}/menus/${menu.id}`)}
              >
                <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                  <UtensilsCrossed className="size-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{menu.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {menu._count?.categories ?? 0} {t("categories_count")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={menu.isActive ? "default" : "secondary"}>
                  {menu.isActive ? t("active_status") : t("inactive_status")}
                </Badge>
                <Switch
                  checked={menu.isActive}
                  onCheckedChange={(checked) =>
                    toggleMutation.mutate({ id: menu.id, isActive: checked })
                  }
                />
                <ConfirmDialog
                  trigger={
                    <Button variant="ghost" size="icon" className="size-8">
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  }
                  title={t("delete_menu")}
                  description={t("delete_menu_desc")}
                  confirmLabel={t("delete")}
                  onConfirm={() => deleteMutation.mutate(menu.id)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/venues/${venueId}/menus/${menu.id}`)}
                >
                  <ArrowRight className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface CrawledItem { name: string; description?: string; price: number; image?: string; }
interface CrawledCategory { name: string; items: CrawledItem[]; }

function ImportMenuDialog({ open, onClose, venueId }: { open: boolean; onClose: () => void; venueId: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mode, setMode] = useState<"url" | "html" | "image">("url");
  const [url, setUrl] = useState("");
  const [rawHtml, setRawHtml] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [importMenuName, setImportMenuName] = useState("");
  const [categories, setCategories] = useState<CrawledCategory[] | null>(null);
  const [error, setError] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Venue planı — image import Pro+ özelliği. FREE plan kullanıcıda buton'un
  // üstünde "Pro" rozeti gösteriyoruz ve tıklamada vision call atmadan
  // direkt upgrade dialog'unu açıyoruz.
  const { data: venueData } = useQuery({
    queryKey: ["venue", venueId],
    queryFn: () => getVenue(venueId),
    enabled: open,
  });
  const isPro = venueData?.planType === "PRO" || venueData?.planType === "BUSINESS";

  const resetState = () => {
    setUrl("");
    setRawHtml("");
    setImages([]);
    setImportMenuName("");
    setCategories(null);
    setError("");
    setMode("url");
  };

  const crawlMutation = useMutation({
    mutationFn: async () => {
      setError("");
      if (mode === "image") {
        if (!images.length) throw new Error(t("crawl_failed"));
        return parseMenuImages(venueId, images);
      }
      if (mode === "html") {
        if (!rawHtml || rawHtml.length < 100) throw new Error(t("crawl_failed"));
        return parseMenu(venueId, rawHtml);
      }
      let html: string | null = null;
      try { const res = await fetch(url); if (res.ok) html = await res.text(); } catch { /* CORS */ }
      if (!html) {
        try { const res = await fetch(`https://api.heremenu.app/api/proxy-fetch?url=${encodeURIComponent(url)}`); if (res.ok) html = await res.text(); } catch { /* */ }
      }
      if (!html) throw new Error(t("crawl_fetch_failed"));
      return parseMenu(venueId, html, url);
    },
    onSuccess: (data) => { setCategories(data.categories); if (!importMenuName) setImportMenuName(t("imported_menu")); },
    onError: (err) => {
      // AI_NOT_AVAILABLE / FEATURE_REQUIRED gibi plan kaynaklı hatalarda
      // saçma string yerine doğrudan "Pro'ya yükselt" dialog'unu aç.
      if (isPlanError(err)) {
        setUpgradeOpen(true);
        return;
      }
      const ax = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
      setError(ax.response?.data?.error || ax.response?.data?.message || ax.message || t("crawl_failed"));
    },
  });

  const importMutation = useMutation({
    mutationFn: () => importMenu(venueId, { menuName: importMenuName, categories: categories! }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["menus", venueId] });
      onClose();
      resetState();
      navigate(`/venues/${venueId}/menus/${data.menuId}`);
    },
    onError: (err) => setError((err as Error).message),
  });

  const totalItems = categories?.reduce((s, c) => s + c.items.length, 0) || 0;

  const removeItem = (ci: number, ii: number) => {
    setCategories(prev => prev ? prev.map((c, i) => i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c).filter(c => c.items.length > 0) : prev);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); resetState(); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="size-5" />
            {t("import_menu")}
          </DialogTitle>
        </DialogHeader>

        {!categories ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("import_menu_desc")}</p>
            <div className="flex gap-2">
              <button className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${mode === "url" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`} onClick={() => setMode("url")}>URL</button>
              <button className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${mode === "html" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`} onClick={() => setMode("html")}>HTML</button>
              {/* "Resimden" Pro+ özelliği. FREE planda buton üstünde "PRO"
                  rozeti var ve TIKLAR TIKLAMAZ upgrade dialog açılıyor;
                  file picker hiç gelmez. Pro+ kullanıcıda normal mode
                  geçişi yapılır. "Analiz Et" tarafında da plan check
                  defensive olarak duruyor. */}
              <button
                className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${mode === "image" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}
                onClick={() => {
                  if (!isPro) {
                    setUpgradeOpen(true);
                    return;
                  }
                  setMode("image");
                }}
              >
                {t("import_from_image")}
                {!isPro && (
                  <span className="absolute -top-2 -right-2 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow-sm">
                    PRO
                  </span>
                )}
              </button>
            </div>
            {mode === "url" && (
              <div className="flex gap-2">
                <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/menu" disabled={crawlMutation.isPending} />
                <Button onClick={() => crawlMutation.mutate()} disabled={!url || crawlMutation.isPending} className="shrink-0">
                  <span className="flex items-center gap-1.5">
                    {crawlMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                    <span>{crawlMutation.isPending ? t("analyzing") : t("analyze")}</span>
                  </span>
                </Button>
              </div>
            )}
            {mode === "html" && (
              <>
                <div>
                  <Label>{t("paste_html")}</Label>
                  <textarea className="mt-1 w-full min-h-[120px] rounded-lg border bg-background p-3 text-xs font-mono" value={rawHtml} onChange={(e) => setRawHtml(e.target.value)} placeholder={t("paste_html_hint")} disabled={crawlMutation.isPending} />
                </div>
                <Button onClick={() => crawlMutation.mutate()} disabled={rawHtml.length < 100 || crawlMutation.isPending}>
                  <span className="flex items-center gap-1.5">
                    {crawlMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                    <span>{crawlMutation.isPending ? t("analyzing") : t("analyze")}</span>
                  </span>
                </Button>
              </>
            )}
            {mode === "image" && (
              <>
                {/* Çoklu görsel seçimi. Sharp arka tarafta 1600px JPEG'e iniyor,
                    yine de kullanıcıya yüklediği dosya boyutunu hatırlatalım. */}
                <div>
                  <Label>{t("select_images")}</Label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    disabled={crawlMutation.isPending}
                    onChange={(e) => {
                      const list = Array.from(e.target.files || []);
                      // Max 10 file ve 5 MB her biri — backend de check ediyor ama
                      // önceden uyaralım, ag-up kullanıcı için kötü UX olmasın.
                      const oversize = list.find((f) => f.size > 5 * 1024 * 1024);
                      if (oversize) {
                        setError(t("image_too_large_5mb"));
                        return;
                      }
                      if (list.length > 10) {
                        setError(t("max_10_images"));
                        return;
                      }
                      setError("");
                      setImages(list);
                    }}
                    className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:opacity-90"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">{t("import_image_hint")}</p>
                </div>
                {images.length > 0 && (
                  <div className="grid grid-cols-5 gap-2">
                    {images.map((f, i) => (
                      <div key={i} className="relative aspect-square overflow-hidden rounded-md border bg-muted">
                        <img src={URL.createObjectURL(f)} alt="" className="size-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setImages(images.filter((_, j) => j !== i))}
                          className="absolute right-1 top-1 rounded-full bg-destructive p-1 text-white opacity-80 hover:opacity-100"
                          aria-label="Kaldır"
                        >
                          <Trash2 className="size-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  onClick={() => {
                    // FREE plana token harcamadan upsell — backend zaten 403
                    // dönecekti ama UX olarak çağrı atmadan dialog açmak
                    // daha düzgün.
                    if (!isPro) {
                      setUpgradeOpen(true);
                      return;
                    }
                    crawlMutation.mutate();
                  }}
                  disabled={!images.length || crawlMutation.isPending}
                >
                  <span className="flex items-center gap-1.5">
                    {crawlMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                    <span>{crawlMutation.isPending ? t("analyzing_images") : t("analyze")}</span>
                  </span>
                </Button>
              </>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <Label>{t("menu_name")}</Label>
                <Input value={importMenuName} onChange={(e) => setImportMenuName(e.target.value)} />
              </div>
              <p className="text-sm text-muted-foreground">{categories.length} {t("categories_count")} · {totalItems} {t("items_count")}</p>
            </div>
            <div className="max-h-[400px] overflow-y-auto space-y-3">
              {categories.map((cat, ci) => (
                <div key={ci} className="rounded-lg border p-3">
                  <p className="font-semibold text-sm mb-2">{cat.name} ({cat.items.length})</p>
                  <div className="space-y-1">
                    {cat.items.map((item, ii) => (
                      <div key={ii} className="flex items-center gap-2 text-sm">
                        {item.image && <img src={item.image} alt="" className="size-8 rounded object-cover shrink-0" />}
                        <span className="flex-1 truncate">{item.name}</span>
                        <span className="font-bold text-primary shrink-0">{item.price > 0 ? `${item.price}` : "-"}</span>
                        <button type="button" className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => removeItem(ci, ii)}>
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCategories(null)}>{t("back")}</Button>
              <Button className="flex-1" onClick={() => importMutation.mutate()} disabled={importMutation.isPending || !importMenuName || totalItems === 0}>
                <span className="flex items-center gap-1.5">
                  {importMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                  <span>{importMutation.isPending ? t("importing") : t("import_items").replace("{{count}}", String(totalItems))}</span>
                </span>
              </Button>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}
      </DialogContent>
      <UpgradeDialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        feature="ai_menu_image_import"
      />
    </Dialog>
  );
}
